import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Heart, RefreshCw, Mail, Calendar, Users, CheckCircle,
  Clock, Send, Edit, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BereavementSupportDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase62/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboardData(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 font-medium">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1 font-medium">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="bereavement-support-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Bereavement Support</h1>
              <p className="text-sm text-slate-600 mt-1 font-medium">13-month automated family support program</p>
            </div>
            <Button variant="outline" onClick={fetchDashboard}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="font-semibold">Dashboard</TabsTrigger>
            <TabsTrigger value="families" className="font-semibold">Families</TabsTrigger>
            <TabsTrigger value="schedule" className="font-semibold">Schedule</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard title="Active Families" value={dashboardData?.stats?.active_families || 0} icon={Users} color="purple" />
              <StatCard title="Emails This Month" value={dashboardData?.stats?.emails_this_month || 0} icon={Mail} color="blue" />
              <StatCard title="Pending Outreach" value={dashboardData?.stats?.pending_outreach || 0} icon={Clock} color="orange" />
              <StatCard title="Completed Programs" value={dashboardData?.stats?.completed_programs || 0} icon={CheckCircle} color="green" />
              <StatCard title="Response Rate" value={`${dashboardData?.stats?.response_rate || 0}%`} icon={Heart} color="pink" />
            </div>

            {/* Upcoming Outreach */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Upcoming Outreach</CardTitle>
                <CardDescription className="text-slate-600 font-medium">Scheduled bereavement support contacts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.upcoming_outreach?.slice(0, 5).map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-lg bg-purple-100">
                          <Mail className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{item.family_name}</p>
                          <p className="text-sm text-slate-600 font-medium">{item.milestone} - {item.type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{new Date(item.scheduled_date).toLocaleDateString()}</Badge>
                        <Button size="sm" variant="outline">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Program Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">13-Month Support Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { month: 1, milestone: "Condolence & Resource Packet", type: "email" },
                    { month: 2, milestone: "Check-in Call", type: "call" },
                    { month: 3, milestone: "Grief Support Resources", type: "email" },
                    { month: 6, milestone: "Mid-Program Check-in", type: "call" },
                    { month: 9, milestone: "Continued Support", type: "email" },
                    { month: 12, milestone: "Anniversary Support", type: "email" },
                    { month: 13, milestone: "Program Completion", type: "email" }
                  ].map((milestone) => (
                    <div key={milestone.month} className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <span className="text-sm font-bold text-slate-900">Month {milestone.month}</span>
                      </div>
                      <div className="h-4 w-4 rounded-full bg-teal-500" />
                      <div className="flex-1 p-3 bg-gray-50 rounded-lg">
                        <p className="font-semibold text-slate-900">{milestone.milestone}</p>
                        <Badge variant="outline" className="mt-1">{milestone.type}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="families" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Active Bereavement Families</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Family</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Patient</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Date of Passing</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Current Month</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Next Outreach</th>
                        <th className="px-4 py-3 text-sm font-bold text-slate-900">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.families?.map((family) => (
                        <tr key={family.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-semibold text-slate-900">{family.family_name}</td>
                          <td className="px-4 py-3 text-slate-700">{family.patient_name}</td>
                          <td className="px-4 py-3 text-slate-600">{new Date(family.date_of_passing).toLocaleDateString()}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline">{family.current_month}/13</Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{family.next_outreach}</td>
                          <td className="px-4 py-3">
                            <Badge className={family.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}>{family.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedule" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-bold">Outreach Schedule</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.upcoming_outreach?.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${item.type === 'call' ? 'bg-green-100' : 'bg-blue-100'}`}>
                          {item.type === 'call' ? (
                            <Calendar className="h-5 w-5 text-green-600" />
                          ) : (
                            <Mail className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{item.family_name}</p>
                          <p className="text-sm text-slate-600">{item.milestone}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge>{new Date(item.scheduled_date).toLocaleDateString()}</Badge>
                        <Button size="sm" variant="outline">
                          <Send className="h-4 w-4 mr-2" />
                          Send Now
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BereavementSupportDashboard;
